const getPdfStatus = (value) => {
  const normalized = typeof value === "string" ? value.trim().toLowerCase() : value;

  if (
    normalized === true ||
    normalized === "true" ||
    normalized === "pass" ||
    normalized === "passed" ||
    normalized === "yes"
  ) {
    return "Pass";
  }

  if (
    normalized === false ||
    normalized === "false" ||
    normalized === "fail" ||
    normalized === "failed" ||
    normalized === "no"
  ) {
    return "Fail";
  }

  return "N/A";
};

export const generateHTMLForPDF = (qaParameters, interactionData, Id) => {
  const overview = interactionData?.overallSummary || {};
  const agentName = overview?.AgentName || "NA";
  const customerName = overview?.CustomerName || interactionData?.customerName || "NA";
  const duration = overview?.CallDuration || "NA";
  const dateTime = interactionData?.createdAt ? new Date(interactionData.createdAt).toLocaleString() : "NA";
  const callIntent = overview?.CallIntent || "NA";
  const callOutcome = overview?.CallOutcome || "NA";
  const callScore = interactionData?.callScore !== undefined ? `${interactionData.callScore}%` : "NA";
  const complianceStatus = overview?.ComplianceStatus ? "Pass" : "Fail";
  const accuracy = overview?.CallAdherence?.pass !== undefined ? `${overview.CallAdherence.pass}%` : "NA";

  // Sort QA parameters by parameterIndex and filter out metadata
  const sortedParameters = Object.entries(qaParameters || {})
    .filter(([key, value]) => value && typeof value === "object" && value.criticality)
    .map(([key, value]) => ({ name: key, ...value }))
    .sort((a, b) => Number(a?.parameterIndex || 0) - Number(b?.parameterIndex || 0));

  // Build rows for Parameter Summary Table (Page 2)
  let summaryRows = "";
  sortedParameters.forEach((param, index) => {
    const criticality = param?.criticality || "NonCritical";
    const originalWeight = param?.originalParameterWeigthage ?? param?.originalParameterWeightage ?? param?.parameterWeightage ?? param?.originalWeightage ?? "NA";
    const subCount = param?.subParameters?.length || 0;

    summaryRows += `
      <tr style="border: 1px solid #000000; page-break-inside: avoid;">
        <td style="padding: 6px 8px; font-size: 12px; text-align: center; border: 1px solid #000000;">${index + 1}</td>
        <td style="padding: 6px 8px; font-size: 12px; font-weight: bold; border: 1px solid #000000;">${param.name.replace(/([a-z])([A-Z])/g, "$1 $2").toUpperCase()}</td>
        <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000;">${criticality}</td>
        <td style="padding: 6px 8px; font-size: 12px; text-align: center; border: 1px solid #000000;">${originalWeight}</td>
        <td style="padding: 6px 8px; font-size: 12px; text-align: center; border: 1px solid #000000;">${subCount}</td>
      </tr>
    `;
  });

  // Build pages for each detailed parameter evaluation (Pages 3+)
  let detailedPages = "";
  sortedParameters.forEach((param, index) => {
    const criticality = param?.criticality || "NonCritical";
    const originalWeight = param?.originalParameterWeigthage ?? param?.originalParameterWeightage ?? param?.parameterWeightage ?? param?.originalWeightage ?? "NA";
    const description = param?.description || "No description provided.";

    // Generate subparameters rows
    let subRows = "";
    (param?.subParameters || []).forEach((sub) => {
      const subStatusValue = sub?.result ?? sub?.issueFlagged;
      const subStatusText = getPdfStatus(subStatusValue);
      const subIsPass = subStatusText === "Pass";
      const subStatusColor = subIsPass ? "#0f766e" : (subStatusText === "Fail" ? "#be123c" : "#64748b");
      const subStatusBg = subIsPass ? "#ccfbf1" : (subStatusText === "Fail" ? "#ffe4e6" : "#f1f5f9");

      const subWeight = sub?.originalWeightage ?? "NA";
      const subScore = sub?.calculatedWeigthage ?? "NA";
      const subRating = sub?.rating !== undefined ? sub.rating : "NA";
      const subDescription = sub?.description || "NA";

      subRows += `
        <tr style="border: 1px solid #000000; page-break-inside: avoid;">
          <td style="padding: 6px 8px; font-size: 11px; font-weight: bold; border: 1px solid #000000; width: 15%;">${sub.parameter}</td>
          <td style="padding: 6px 8px; font-size: 11px; border: 1px solid #000000; width: 10%;">${sub.criticality || "NonCritical"}</td>
          <td style="padding: 6px 8px; font-size: 11px; border: 1px solid #000000; width: 22%; line-height: 1.3;">${subDescription}</td>
          <td style="padding: 6px 8px; font-size: 11px; border: 1px solid #000000; width: 20%; line-height: 1.3;">${sub.expectation || "NA"}</td>
          <td style="padding: 6px 8px; font-size: 11px; text-align: center; border: 1px solid #000000; width: 10%;">
            <span style="padding: 2px 6px; border-radius: 4px; font-weight: bold; color: ${subStatusColor}; background-color: ${subStatusBg}; border: 1px solid ${subStatusColor};">${subStatusText}</span>
          </td>
          <td style="padding: 6px 8px; font-size: 11px; border: 1px solid #000000; width: 15%; line-height: 1.3;">${sub.reason || "NA"}</td>
          <td style="padding: 6px 8px; font-size: 11px; font-weight: bold; text-align: center; border: 1px solid #000000; width: 8%;">${subScore}/${subWeight} (R: ${subRating})</td>
        </tr>
      `;
    });

    detailedPages += `
      <div style="padding-top: 10px;">
        <div style="page-break-inside: avoid; page-break-after: avoid; margin-bottom: 12px;">
          ${index === 0 ? '<h2 style="margin: 0 0 15px 0; font-size: 18px; font-weight: bold; page-break-after: avoid;">Detailed Parameters</h2>' : ''}
          <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: bold; page-break-after: avoid;">${index + 1}. ${param.name.replace(/([a-z])([A-Z])/g, "$1 $2").toUpperCase()}</h3>
          
          <div style="line-height: 1.4; font-size: 12px; page-break-after: avoid;">
            <div><strong>Criticality:</strong> ${criticality}</div>
            <div><strong>Original Parameter Weightage:</strong> ${originalWeight}</div>
            <div style="margin-top: 3px;"><strong>Description:</strong> ${description}</div>
          </div>
        </div>

        <table style="width: 100%; border-collapse: collapse; border: 1px solid #000000; page-break-inside: auto;">
          <thead style="display: table-header-group;">
            <tr style="background-color: #ffffff; border: 1px solid #000000; text-align: left; page-break-inside: avoid;">
              <th style="padding: 6px 8px; font-weight: bold; font-size: 11px; border: 1px solid #000000;">Subparameter</th>
              <th style="padding: 6px 8px; font-weight: bold; font-size: 11px; border: 1px solid #000000;">Criticality</th>
              <th style="padding: 6px 8px; font-weight: bold; font-size: 11px; border: 1px solid #000000;">Description</th>
              <th style="padding: 6px 8px; font-weight: bold; font-size: 11px; border: 1px solid #000000;">Expectation</th>
              <th style="padding: 6px 8px; font-weight: bold; font-size: 11px; border: 1px solid #000000; text-align: center;">Status</th>
              <th style="padding: 6px 8px; font-weight: bold; font-size: 11px; border: 1px solid #000000;">AI Reason</th>
              <th style="padding: 6px 8px; font-weight: bold; font-size: 11px; border: 1px solid #000000; text-align: center;">Score</th>
            </tr>
          </thead>
          <tbody>
            ${subRows}
          </tbody>
        </table>
      </div>
      ${index < sortedParameters.length - 1 ? '<div class="html2pdf__page-break"></div>' : ''}
    `;
  });

  return `
    <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #000000; padding: 0; line-height: 1.4; background-color: #ffffff;">
      
      <!-- PAGE 1: PARAMETER TEMPLATE / INFO -->
      <div>
        <h1 style="margin: 0 0 10px 0; font-size: 22px; font-weight: bold;">QA Evaluation Parameter Template</h1>
        <p style="margin: 0 0 20px 0; font-size: 12px; line-height: 1.5; color: #333333;">
          This document describes the QA evaluation structure used for sales/advisory call analysis. It 
          is generated from output-template.json and can be shared with developers and business 
          stakeholders as the reference for parameter names, subparameters, criticality, expectations, 
          and weightage.
        </p>

        <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: bold;">Scoring Fields</h3>
        <table style="width: 100%; border-collapse: collapse; border: 1px solid #000000; margin-bottom: 20px;">
          <thead>
            <tr style="border: 1px solid #000000; text-align: left; page-break-inside: avoid;">
              <th style="padding: 6px 8px; font-weight: bold; font-size: 12px; border: 1px solid #000000; width: 30%;">Field</th>
              <th style="padding: 6px 8px; font-weight: bold; font-size: 12px; border: 1px solid #000000; width: 70%;">Meaning</th>
            </tr>
          </thead>
          <tbody>
            <tr style="border: 1px solid #000000; page-break-inside: avoid;">
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000; font-family: monospace;">criticality</td>
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000;">Indicates whether the parameter or subparameter is critical to the evaluation.</td>
            </tr>
            <tr style="border: 1px solid #000000; page-break-inside: avoid;">
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000; font-family: monospace;">description</td>
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000;">Explains what the parameter or subparameter evaluates.</td>
            </tr>
            <tr style="border: 1px solid #000000; page-break-inside: avoid;">
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000; font-family: monospace;">expectation</td>
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000;">Defines the exact standard expected from the call.</td>
            </tr>
            <tr style="border: 1px solid #000000; page-break-inside: avoid;">
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000; font-family: monospace;">originalParameterWeigthage</td>
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000;">Maximum parameter-level weightage from the template.</td>
            </tr>
            <tr style="border: 1px solid #000000; page-break-inside: avoid;">
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000; font-family: monospace;">originalWeightage</td>
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000;">Maximum subparameter-level weightage from the template.</td>
            </tr>
            <tr style="border: 1px solid #000000; page-break-inside: avoid;">
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000; font-family: monospace;">rating</td>
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000;">Evaluation score from 1 to 5, populated during call analysis.</td>
            </tr>
            <tr style="border: 1px solid #000000; page-break-inside: avoid;">
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000; font-family: monospace;">calculatedWeigthage</td>
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000;">Calculated score for a subparameter, usually originalWeightage * rating / 5.</td>
            </tr>
            <tr style="border: 1px solid #000000; page-break-inside: avoid;">
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000; font-family: monospace;">issueFlagged</td>
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000;">true when the expectation is not met; false when it is met.</td>
            </tr>
            <tr style="border: 1px solid #000000; page-break-inside: avoid;">
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000; font-family: monospace;">reason</td>
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000;">Evaluation rationale populated during call analysis.</td>
            </tr>
            <tr style="border: 1px solid #000000; page-break-inside: avoid;">
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000; font-family: monospace;">timeStamp</td>
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000;">Transcript timestamp used as supporting evidence during call analysis.</td>
            </tr>
          </tbody>
        </table>

        <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: bold;">Rating Scale</h3>
        <table style="width: 100%; border-collapse: collapse; border: 1px solid #000000;">
          <thead>
            <tr style="border: 1px solid #000000; text-align: left; page-break-inside: avoid;">
              <th style="padding: 6px 8px; font-weight: bold; font-size: 12px; border: 1px solid #000000; width: 15%;">Rating</th>
              <th style="padding: 6px 8px; font-weight: bold; font-size: 12px; border: 1px solid #000000; width: 85%;">Interpretation</th>
            </tr>
          </thead>
          <tbody>
            <tr style="border: 1px solid #000000; page-break-inside: avoid;">
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000; font-weight: bold; text-align: center;">5</td>
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000;">Excellent: all expected elements are present and executed strongly.</td>
            </tr>
            <tr style="border: 1px solid #000000; page-break-inside: avoid;">
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000; font-weight: bold; text-align: center;">4</td>
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000;">Good: most expected elements are present with minor gaps.</td>
            </tr>
            <tr style="border: 1px solid #000000; page-break-inside: avoid;">
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000; font-weight: bold; text-align: center;">3</td>
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000;">Acceptable: core elements are present but notable gaps remain.</td>
            </tr>
            <tr style="border: 1px solid #000000; page-break-inside: avoid;">
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000; font-weight: bold; text-align: center;">2</td>
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000;">Poor: significant elements are missing or weakly handled.</td>
            </tr>
            <tr style="border: 1px solid #000000; page-break-inside: avoid;">
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000; font-weight: bold; text-align: center;">1</td>
              <td style="padding: 6px 8px; font-size: 12px; border: 1px solid #000000;">Unacceptable: critical elements are absent or not meaningfully addressed.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="html2pdf__page-break"></div>

      <!-- PAGE 2: PARAMETER SUMMARY -->
      <div>
        <h2 style="margin: 0 0 15px 0; font-size: 18px; font-weight: bold;">Parameter Summary</h2>
        <table style="width: 100%; border-collapse: collapse; border: 1px solid #000000; page-break-inside: auto;">
          <thead>
            <tr style="background-color: #ffffff; border: 1px solid #000000; text-align: left; page-break-inside: avoid;">
              <th style="padding: 6px 8px; font-weight: bold; font-size: 12px; border: 1px solid #000000; text-align: center; width: 8%;">#</th>
              <th style="padding: 6px 8px; font-weight: bold; font-size: 12px; border: 1px solid #000000; width: 45%;">Parameter</th>
              <th style="padding: 6px 8px; font-weight: bold; font-size: 12px; border: 1px solid #000000; width: 20%;">Criticality</th>
              <th style="padding: 6px 8px; font-weight: bold; font-size: 12px; border: 1px solid #000000; text-align: center; width: 12%;">Weightage</th>
              <th style="padding: 6px 8px; font-weight: bold; font-size: 12px; border: 1px solid #000000; text-align: center; width: 15%;">Subparameters</th>
            </tr>
          </thead>
          <tbody>
            ${summaryRows}
          </tbody>
        </table>
      </div>

      <div class="html2pdf__page-break"></div>

      <!-- PAGES 3+: DETAILED PARAMETERS -->
      ${detailedPages}

    </div>
  `;
};
